import * as Sentry from '@sentry/react';

/**
 * Minimal shape needed to identify a user to Sentry. Deliberately loose so it
 * accepts the `/api/me` payload (`AuthUser` / `CurrentUser`) without coupling
 * this helper to those types. All fields allow `null`/`undefined` because the
 * upstream payload does.
 */
export interface SentryUserInput {
  readonly id?: string | number | null | undefined;
  readonly email?: string | null | undefined;
  readonly fullName?: string | null | undefined;
  readonly firstName?: string | null | undefined;
  readonly lastName?: string | null | undefined;
}

const buildUsername = (user: SentryUserInput): string | undefined => {
  if (typeof user.fullName === 'string' && user.fullName.trim() !== '') {
    return user.fullName.trim();
  }

  const composed = [user.firstName, user.lastName]
    .filter((part): part is string => typeof part === 'string' && part.trim() !== '')
    .join(' ')
    .trim();

  return composed !== '' ? composed : undefined;
};

/**
 * Attaches the authenticated user to the active Sentry scope so error events
 * are grouped by a real person (id / username / email) instead of falling back
 * to the request IP address.
 *
 * Safe to call when Sentry is not initialised (e.g. local dev without a DSN):
 * `Sentry.setUser` is a no-op in that case. Bails out when there is no stable
 * `id` so we never send a half-populated identity.
 */
export const setSentryUser = (user: SentryUserInput | null | undefined): void => {
  if (!user || user.id === null || user.id === undefined) {
    return;
  }

  const username = buildUsername(user);

  Sentry.setUser({
    id: String(user.id),
    ...(user.email ? { email: user.email } : {}),
    ...(username ? { username } : {}),
  });
};

/**
 * Detaches the user from the Sentry scope. Call on logout or whenever the
 * session is invalidated so subsequent events are not misattributed to the
 * previous user.
 */
export const clearSentryUser = (): void => {
  Sentry.setUser(null);
};
